import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import {
  getMeetingCriteriaScores,
  upsertConsensusScores,
} from "../api/meeting-api";
import type {
  CriteriaId,
  MeetingCriteriaScore,
  UpsertConsensusScoresRequest,
} from "../types/meeting.type";
import { meetingScoresProgressKeys } from "./useMeetingScoresProgress";

const STALE_TIME = 30_000;

export const meetingScoresKeys = {
  scores: (rfxId: number, evalId: number, criteriaId: CriteriaId) =>
    ["meetingScores", rfxId, evalId, criteriaId] as const,
};

interface UseMeetingScoresResult {
  data: MeetingCriteriaScore | undefined;
  isLoading: boolean;
  upsertScores: UseMutationResult<MeetingCriteriaScore, Error, UpsertConsensusScoresRequest>;
}

export const useMeetingScores = (
  rfxId: number | undefined,
  evalId: number | undefined,
  criteriaId: CriteriaId | undefined
): UseMeetingScoresResult => {
  const queryClient = useQueryClient();

  const isNumericCriteria = typeof criteriaId === "number";

  const query: UseQueryResult<MeetingCriteriaScore> = useQuery({
    queryKey: meetingScoresKeys.scores(rfxId!, evalId!, criteriaId!),
    queryFn: () => getMeetingCriteriaScores(rfxId!, evalId!, criteriaId as number),
    enabled: !!rfxId && !!evalId && isNumericCriteria,
    staleTime: STALE_TIME,
  });

  const upsertScoresMutation = useMutation({
    mutationFn: (payload: UpsertConsensusScoresRequest) =>
      upsertConsensusScores(rfxId!, evalId!, criteriaId as number, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: meetingScoresKeys.scores(rfxId!, evalId!, criteriaId!),
      });
      queryClient.invalidateQueries({
        queryKey: meetingScoresProgressKeys.progress(rfxId!, evalId!),
      });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    upsertScores: upsertScoresMutation,
  };
};
